import { useState, useEffect, useRef } from "react";
import "./PreTripInspection.css";

export default function PreTripInspection({ onHome, defaultView = "document" }) {
  const [activeView, setActiveView] = useState(defaultView);
  const [gaugeValue, setGaugeValue] = useState(0);
  const [cardIdx, setCardIdx] = useState(0);
  const [deck, setDeck] = useState(flashcards);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [quizIdx, setQuizIdx] = useState(0);
  const [quizScore, setQuizScore] = useState(0);
  const [quiz, setQuiz] = useState(quizData);
  const [answered, setAnswered] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [speechRate, setSpeechRate] = useState(1);
  const [voiceStatus, setVoiceStatus] = useState("");
  const [voiceAvailable, setVoiceAvailable] = useState(true);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(-1);
  const [pausedChunkIndex, setPausedChunkIndex] = useState(-1);
  const [allChunks, setAllChunks] = useState([]);
  const [chunkToItemMap, setChunkToItemMap] = useState({});
  const [highlightedRowRef, setHighlightedRowRef] = useState(null);
  const [documentLines, setDocumentLines] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [repeatMode, setRepeatMode] = useState(false);
  const [repeatCount, setRepeatCount] = useState(0);
  const [showBusGuide, setShowBusGuide] = useState(false);
  const repeatModeRef = useRef(false);

  useEffect(() => {
    repeatModeRef.current = repeatMode;
  }, [repeatMode]);

  const colorFor = (v) => (v <= 45 ? "#c8372f" : v <= 100 ? "#ffb020" : v <= 140 ? "#4caf7d" : "#3a4048");

  const getBusStatus = (text) => {
    if (!text) return null;
    const statuses = getBusStatuses(text);
    return statuses.length > 0 ? statuses[0] : null;
  };

  const getBusStatuses = (text) => {
    if (!text) return [];
    const t = text.toLowerCase();
    const statuses = [];

    // Check for all statuses in the text (can have multiple)
    if (t.includes("o.d.s") || t.includes("ods the bus")) statuses.push("ODS");
    if (t.includes("turn the bus to night run") || t.includes("turn to night run") || t.includes("back to night run")) statuses.push("NIGHT_RUN");
    if (t.includes("start the bus") || t.includes("air pressure rate build-up") || t.includes("governor cut-in") || t.includes("build to a full tank")) statuses.push("START");
    if (t.includes("chock the bus") || t.includes("chock")) statuses.push("CHOCK");
    if (t.includes("stop-engine") || t.includes("master run switch into the stop") || t.includes("turn the master run switch") && t.includes("stop")) statuses.push("OFF");
    if (t.includes("back the bus up") && t.includes("turn the bus off")) statuses.push("OFF");
    if (t.includes("turn the bus off") || (t.includes("turn off") && t.includes("engine"))) statuses.push("OFF");
    if (t.includes("keep engine off")) statuses.push("OFF");
    // Only mark as OFF if releasing parking brake AND it's not a governor/leak test
    if ((t.includes("release the parking brake") || t.includes("apply firm pressure")) && !t.includes("test") && !t.includes("governor") && !t.includes("leak")) statuses.push("OFF");
    if (t.includes("parking brake test") || t.includes("service brake test")) statuses.push("RUNNING");

    // Remove duplicates
    return [...new Set(statuses)];
  };

  const getBusStatusBadge = (status) => {
    if (!status) return null;
    const badges = {
      "START": { icon: "🟢", label: "START BUS", color: "#4caf7d" },
      "NIGHT_RUN": { icon: "🟡", label: "NIGHT RUN", color: "#ffb020" },
      "RUNNING": { icon: "▶️", label: "ENGINE ON", color: "#3498db" },
      "OFF": { icon: "⏹️", label: "ENGINE OFF", color: "#c8372f" },
      "CHOCK": { icon: "🔒", label: "CHOCK BUS", color: "#f39c12" },
      "ODS": { icon: "🔴", label: "O.D.S", color: "#e74c3c" },
    };
    return badges[status];
  };

  const getNeedleAngle = (psi) => {
    const clamp = Math.max(0, Math.min(150, psi));
    if (clamp <= 55) return -90 + (clamp / 55) * 59.7;
    if (clamp <= 100) return -30.3 + ((clamp - 55) / 45) * 30.3;
    if (clamp <= 130) return 0 + ((clamp - 100) / 30) * 34.7;
    return 34.7 + ((clamp - 130) / 20) * 55.3;
  };
  const needleAngle = getNeedleAngle(gaugeValue);

  const goToNextSection = () => {
    if (!selectedSection) return;

    const section = docSections[selectedSection];
    let nextSectionId = selectedSection;
    let nextSubsectionName = selectedSubsection;

    if (section.subsections) {
      const subsectionNames = section.subsections.map(s => s.name);
      const currentSubsectionIndex = selectedSubsection
        ? subsectionNames.indexOf(selectedSubsection)
        : -1;

      if (currentSubsectionIndex >= 0 && currentSubsectionIndex < subsectionNames.length - 1) {
        nextSubsectionName = subsectionNames[currentSubsectionIndex + 1];
      } else {
        const sectionIds = Object.keys(docSections);
        const currentSectionIndex = sectionIds.indexOf(selectedSection);
        if (currentSectionIndex >= 0 && currentSectionIndex < sectionIds.length - 1) {
          nextSectionId = sectionIds[currentSectionIndex + 1];
          nextSubsectionName = null;
        }
      }
    } else {
      const sectionIds = Object.keys(docSections);
      const currentSectionIndex = sectionIds.indexOf(selectedSection);
      if (currentSectionIndex >= 0 && currentSectionIndex < sectionIds.length - 1) {
        nextSectionId = sectionIds[currentSectionIndex + 1];
        nextSubsectionName = null;
      }
    }

    stopSpeaking();
    setSelectedSection(nextSectionId);
    setSelectedSubsection(nextSubsectionName);
    setRepeatCount(1);

    setTimeout(() => {
      const nextSection = docSections[nextSectionId];
      let linesToSpeak = [];

      if (nextSubsectionName && nextSection.subsections) {
        linesToSpeak = generateSubsectionLines(nextSectionId, nextSubsectionName);
      } else if (nextSectionId) {
        linesToSpeak = generateSectionLines(nextSectionId);
      }

      if (linesToSpeak.length > 0) {
        setDocumentLines(linesToSpeak);
        setCurrentChunkIndex(-1);
        setPausedChunkIndex(-1);
        speakLines(linesToSpeak, 0);
      }
    }, 100);
  };

  const shuffleQuiz = () => {
    const shuffled = [...quizData];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setQuiz(shuffled);
  };

  const handleQuizAnswer = (optIdx) => {
    setAnswered(optIdx);
    if (optIdx === quiz[quizIdx].correct) {
      setQuizScore(quizScore + 1);
    }
  };

  const nextQuestion = () => {
    if (quizIdx + 1 >= quiz.length) {
      setQuizIdx(quiz.length);
    } else {
      setQuizIdx(quizIdx + 1);
      setAnswered(null);
    }
  };

  useEffect(() => {
    // Auto-scroll to keep highlighted line visible
    if (highlightedRowRef && activeView === "document" && (isSpeaking || isPaused)) {
      setTimeout(() => {
        highlightedRowRef.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [currentChunkIndex, highlightedRowRef, activeView, isSpeaking, isPaused]);

  const restartQuiz = () => {
    setQuizIdx(0);
    setQuizScore(0);
    shuffleQuiz();
    setAnswered(null);
  };

  const generateDocumentLines = () => {
    const lines = [];
    Object.entries(docSections).forEach(([id, section]) => {
      lines.push({ type: "title", text: section.title });
      if (section.subsections) {
        section.subsections.forEach(subsection => {
          lines.push({ type: "subsection", text: subsection.name });
          subsection.items.forEach(item => {
            lines.push({ type: "item", text: item.text, critical: item.critical, sectionId: id, subsectionName: subsection.name, busStatus: getBusStatus(item.text) });
          });
        });
      } else if (section.items) {
        section.items.forEach(item => {
          lines.push({ type: "item", text: item.text, critical: item.critical, sectionId: id, busStatus: getBusStatus(item.text) });
        });
      }
    });
    return lines;
  };

  const generateSectionLines = (sectionId) => {
    const lines = [];
    if (docSections[sectionId]) {
      const section = docSections[sectionId];
      lines.push({ type: "title", text: section.title });
      if (section.subsections) {
        section.subsections.forEach(subsection => {
          lines.push({ type: "subsection", text: subsection.name });
          subsection.items.forEach(item => {
            lines.push({ type: "item", text: item.text, critical: item.critical, sectionId: sectionId, subsectionName: subsection.name, busStatus: getBusStatus(item.text) });
          });
        });
      } else if (section.items) {
        section.items.forEach(item => {
          lines.push({ type: "item", text: item.text, critical: item.critical, sectionId: sectionId, busStatus: getBusStatus(item.text) });
        });
      }
    }
    return lines;
  };

  const generateSubsectionLines = (sectionId, subsectionName) => {
    const lines = [];
    if (docSections[sectionId]?.subsections) {
      const section = docSections[sectionId];
      lines.push({ type: "title", text: section.title });
      const subsection = section.subsections.find(s => s.name === subsectionName);
      if (subsection) {
        lines.push({ type: "subsection", text: subsection.name });
        subsection.items.forEach(item => {
          lines.push({ type: "item", text: item.text, critical: item.critical, sectionId: sectionId, subsectionName: subsection.name, busStatus: getBusStatus(item.text) });
        });
      }
    }
    return lines;
  };

  const generateDocumentText = () => {
    let text = "Pre-Trip Inspection Guide. ";
    Object.entries(docSections).forEach(([id, section]) => {
      text += `\n${section.title}. `;
      if (section.subsections) {
        section.subsections.forEach(subsection => {
          text += `${subsection.name}. `;
          subsection.items.forEach(item => {
            text += `${item.text}. `;
          });
        });
      } else if (section.items) {
        section.items.forEach(item => {
          text += `${item.text}. `;
        });
      }
    });
    return text;
  };

  const getGoodVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    // Try to find a high-quality female voice (typically clearer for educational content)
    let preferredVoice = voices.find(voice =>
      voice.name.includes('Google US English') ||
      voice.name.includes('Samantha') ||
      voice.name.includes('Victoria') ||
      voice.name.includes('Karen')
    );
    // Fallback to any female voice
    if (!preferredVoice) {
      preferredVoice = voices.find(voice => voice.name.includes('Female') || voice.lang.startsWith('en-US'));
    }
    // Final fallback to any available voice
    if (!preferredVoice && voices.length > 0) {
      preferredVoice = voices[0];
    }
    return preferredVoice;
  };

  const speakLines = (lines, index = 0) => {
    if (index >= lines.length) {
      if (repeatModeRef.current) {
        setRepeatCount(prev => prev + 1);
        setVoiceStatus(`🔁 Repeating...`);
        setTimeout(() => speakLines(lines, 0), 1000);
      } else {
        setIsSpeaking(false);
        setIsPaused(false);
        setCurrentChunkIndex(-1);
        setPausedChunkIndex(-1);
        setVoiceStatus("✅ Finished");
        setTimeout(() => setVoiceStatus(""), 2000);
      }
      return;
    }

    try {
      setCurrentChunkIndex(index);
      const line = lines[index];
      const utterance = new SpeechSynthesisUtterance(line.text);
      utterance.rate = speechRate;
      utterance.pitch = 1;
      utterance.volume = 1;
      utterance.lang = 'en-US';
      utterance.voice = getGoodVoice();

      utterance.onstart = () => {
        setIsSpeaking(true);
        setIsPaused(false);
        setVoiceStatus(`🔊 Playing... (${index + 1}/${lines.length})`);
      };

      utterance.onend = () => {
        speakLines(lines, index + 1);
      };

      utterance.onerror = (event) => {
        setIsSpeaking(false);
        setVoiceStatus(`❌ Error: ${event.error}`);
        setTimeout(() => setVoiceStatus(""), 3000);
      };

      window.speechSynthesis.speak(utterance);
    } catch (error) {
      setVoiceStatus(`❌ Error: ${error.message}`);
      setIsSpeaking(false);
    }
  };

  const speak = (useLines = false) => {
    if (!('speechSynthesis' in window)) {
      setVoiceStatus("❌ Voice not supported on this browser");
      setVoiceAvailable(false);
      return;
    }

    try {
      window.speechSynthesis.cancel();
      setVoiceStatus("🔊 Starting...");
      let lines = [];
      if (selectedSection && selectedSubsection) {
        lines = generateSubsectionLines(selectedSection, selectedSubsection);
      } else if (selectedSection) {
        lines = generateSectionLines(selectedSection);
      } else {
        lines = generateDocumentLines();
      }
      const mapping = {};
      lines.forEach((line, idx) => {
        mapping[idx] = {
          type: line.type,
          text: line.text,
          sectionId: line.sectionId,
          critical: line.critical,
          subsectionName: line.subsectionName
        };
      });
      setAllChunks(lines.map(l => l.text));
      setChunkToItemMap(mapping);
      setDocumentLines(lines);
      setCurrentChunkIndex(-1);
      setPausedChunkIndex(-1);
      speakLines(lines);
    } catch (error) {
      setVoiceStatus(`❌ Error: ${error.message}`);
      setIsSpeaking(false);
    }
  };

  const pauseSpeaking = () => {
    if (!('speechSynthesis' in window)) return;

    try {
      if (isPaused) {
        // Resume from pause - restart from paused position
        window.speechSynthesis.cancel();
        setIsSpeaking(true);
        setIsPaused(false);
        setVoiceStatus(`🔊 Resuming... (${currentChunkIndex + 1}/${documentLines.length})`);
        // Restart from the paused line
        speakLines(documentLines, currentChunkIndex);
      } else if (isSpeaking) {
        // Pause while playing
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
        setIsPaused(true);
        setVoiceStatus("⏸️ Paused");
      }
    } catch (error) {
      setVoiceStatus(`❌ Error: ${error.message}`);
      setIsSpeaking(false);
    }
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setIsPaused(false);
      setCurrentChunkIndex(-1);
      setPausedChunkIndex(-1);
      setVoiceStatus("⏹️ Stopped");
      setTimeout(() => setVoiceStatus(""), 1500);
    }
  };

  const handleReadDocument = () => {
    if (isSpeaking || isPaused) {
      pauseSpeaking();
    } else {
      speak(true);
    }
  };

  const nextCard = () => setCardIdx((cardIdx + 1) % deck.length);
  const prevCard = () => setCardIdx((cardIdx - 1 + deck.length) % deck.length);
  const shuffleDeck = () => {
    const shuffled = [...flashcards];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setDeck(shuffled);
    setCardIdx(0);
  };

  return (
    <div className="pretrip-container">
      <header className="pretrip-header">
        <button className="pretrip-back-btn" onClick={onHome}>← Back</button>
        <h1>Pre-Trip <span>Inspection</span></h1>
        <p>Dashboard · Flashcards · Quiz – get road-ready for test day</p>
      </header>

      <nav className="pretrip-nav">
        {["dashboard", "flashcards", "quiz", "document", "studyplan"].map((view) => (
          <button
            key={view}
            className={`pretrip-tab ${activeView === view ? "active" : ""}`}
            onClick={() => setActiveView(view)}
          >
            {view === "studyplan" ? "Study Plan" : view.charAt(0).toUpperCase() + view.slice(1)}
          </button>
        ))}
      </nav>

      <main className="pretrip-main">
        {/* DASHBOARD */}
        {activeView === "dashboard" && (
          <section className="pretrip-view">
            <h2 className="pretrip-section-title">Air Pressure Gauge</h2>
            <div className="pretrip-gauge-wrap">
              <svg className="pretrip-gauge-svg" viewBox="0 0 200 130">
                {/* Color zones */}
                <path d="M20,110 A80,80 0 0,1 55,32" fill="none" stroke="#c8372f" strokeWidth="16"/>
                <path d="M55,32 A80,80 0 0,1 100,20" fill="none" stroke="#ffb020" strokeWidth="16"/>
                <path d="M100,20 A80,80 0 0,1 150,38" fill="none" stroke="#4caf7d" strokeWidth="16"/>
                <path d="M150,38 A80,80 0 0,1 180,110" fill="none" stroke="#3a4048" strokeWidth="16"/>

                {/* Tick labels */}
                <text x="14" y="122" fill="#d9dbe0" fontSize="9">0</text>
                <text x="50" y="30" fill="#d9dbe0" fontSize="9">55</text>
                <text x="95" y="14" fill="#d9dbe0" fontSize="9">100</text>
                <text x="150" y="30" fill="#d9dbe0" fontSize="9">130</text>
                <text x="178" y="122" fill="#d9dbe0" fontSize="9">150</text>

                {/* Needle */}
                <g style={{ transform: `rotate(${needleAngle}deg)`, transformOrigin: "100px 110px", transition: "transform 0.6s" }}>
                  <line x1="100" y1="110" x2="100" y2="42" stroke="#f2f1ea" strokeWidth="3"/>
                  <circle cx="100" cy="110" r="6" fill="#f2f1ea"/>
                </g>

                {/* PSI Display */}
                <text x="100" y="128" fill="#ffb020" fontSize="11" fontWeight="700" textAnchor="middle">{gaugeValue} psi</text>
              </svg>

              <div className="pretrip-gauge-legend">
                <h3>Tap a Threshold</h3>
                {gaugeStops.map((s) => (
                  <div
                    key={s.value}
                    className="pretrip-glabel"
                    onClick={() => setGaugeValue(s.value)}
                    role="button"
                    tabIndex="0"
                  >
                    <span className="pretrip-swatch" style={{ background: colorFor(s.value) }}></span>
                    <div className="pretrip-glabel-text">
                      <b>{s.value} psi</b>
                      <small>{s.label}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <h2 className="pretrip-section-title">Key Numbers</h2>
            <div className="pretrip-fact-grid">
              {facts.map(([k, v], i) => (
                <div key={i} className="pretrip-fact-card">
                  <h4>{k}</h4>
                  <p>{v}</p>
                </div>
              ))}
            </div>

            <h2 className="pretrip-section-title">Phrases To Announce</h2>
            <div className="pretrip-phrase-list">
              {phrases.map(([k, v], i) => (
                <div key={i} className="pretrip-phrase">
                  <b>{k}</b>
                  <em>{v}</em>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FLASHCARDS */}
        {activeView === "flashcards" && (
          <section className="pretrip-view">
            <div className="pretrip-deck-bar">
              <span>Card {cardIdx + 1} / {deck.length}</span>
              <span>Click card to flip</span>
            </div>
            <div className="pretrip-card-stage">
              <div
                className={`pretrip-flip-card ${cardFlipped ? "flipped" : ""}`}
                onClick={() => setCardFlipped(!cardFlipped)}
              >
                <div className="pretrip-face front">
                  <span className="pretrip-tag">Question</span>
                  <div className="pretrip-q">{deck[cardIdx][0]}</div>
                  <div className="pretrip-hint">tap to reveal answer</div>
                </div>
                <div className="pretrip-face back">
                  <span className="pretrip-tag">Answer</span>
                  <div className="pretrip-a">{deck[cardIdx][1]}</div>
                  <div className="pretrip-hint">tap to flip back</div>
                </div>
              </div>
            </div>
            <div className="pretrip-deck-controls">
              <button onClick={prevCard}>← Prev</button>
              <button onClick={shuffleDeck}>Shuffle</button>
              <button onClick={nextCard} className="primary">Next →</button>
            </div>
          </section>
        )}

        {/* QUIZ */}
        {activeView === "quiz" && (
          <section className="pretrip-view">
            {quizIdx < quiz.length ? (
              <>
                <div className="pretrip-quiz-top">
                  <span>Question {quizIdx + 1} / {quiz.length}</span>
                  <span className="pretrip-score">Score: {quizScore}</span>
                </div>
                <div className="pretrip-qbox">
                  <div className="pretrip-qtext">{quiz[quizIdx].q}</div>
                  <div className="pretrip-opts-wrap">
                    {quiz[quizIdx].opts.map((opt, i) => (
                      <button
                        key={i}
                        className={`pretrip-opt ${
                          answered !== null
                            ? i === quiz[quizIdx].correct
                              ? "correct"
                              : i === answered
                              ? "wrong"
                              : ""
                            : ""
                        }`}
                        onClick={() => handleQuizAnswer(i)}
                        disabled={answered !== null}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {answered !== null && (
                    <button className="pretrip-next" onClick={nextQuestion}>
                      Next →
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="pretrip-quiz-done">
                <h3>{quizScore} / {quiz.length}</h3>
                <p>
                  {quizScore === quiz.length
                    ? "Perfect run – you're ready."
                    : "Review the misses, then run it back."}
                </p>
                <button onClick={restartQuiz}>Restart Quiz</button>
              </div>
            )}
          </section>
        )}

        {/* DOCUMENT */}
        {activeView === "document" && (
          <section className="pretrip-view">
            <div className="pretrip-doc-audio-controls">
              <div className="pretrip-audio-buttons">
                <button
                  className={`pretrip-audio-btn ${isSpeaking ? "playing" : ""} ${isPaused ? "paused" : ""}`}
                  onClick={handleReadDocument}
                  title={isSpeaking ? "Pause reading" : isPaused ? "Resume reading" : "Read document aloud"}
                  disabled={!voiceAvailable}
                >
                  {isSpeaking ? "⏸️ Pause" : isPaused ? "▶️ Resume" : "🔊 Read Aloud"}
                </button>
                {(isSpeaking || isPaused) && (
                  <button
                    className="pretrip-audio-stop-btn"
                    onClick={stopSpeaking}
                    title="Stop reading completely"
                  >
                    ⏹️ Stop
                  </button>
                )}
                <button
                  className="pretrip-audio-guide-btn"
                  onClick={() => setShowBusGuide(true)}
                  title="View bus start/stop sequence guide"
                >
                  📋 Bus Guide
                </button>
              </div>
              <div className="pretrip-audio-speed">
                <label htmlFor="speech-rate">Speed:</label>
                <input
                  id="speech-rate"
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.25"
                  value={speechRate}
                  onChange={(e) => setSpeechRate(parseFloat(e.target.value))}
                  disabled={isSpeaking || isPaused}
                />
                <span>{speechRate.toFixed(2)}x</span>
              </div>
              {voiceStatus && (
                <div className="pretrip-voice-status">
                  {voiceStatus}
                </div>
              )}
            </div>

            <div className="pretrip-section-selector">
              <h3>Select Section to Study</h3>
              <div className="pretrip-section-buttons">
                <button
                  className={`pretrip-section-btn ${selectedSection === null ? "active" : ""}`}
                  onClick={() => {
                    setSelectedSection(null);
                    stopSpeaking();
                  }}
                >
                  📋 Full Document
                </button>
                {Object.entries(docSections).map(([id, section]) => (
                  <button
                    key={id}
                    className={`pretrip-section-btn ${selectedSection === id ? "active" : ""}`}
                    onClick={() => {
                      setSelectedSection(id);
                      stopSpeaking();
                      setCurrentChunkIndex(-1);
                    }}
                    style={{ borderColor: selectedSection === id ? section.color : "transparent" }}
                  >
                    <span className="pretrip-section-icon">{section.icon}</span>
                    <span className="pretrip-section-name">{id.charAt(0).toUpperCase() + id.slice(1)}</span>
                  </button>
                ))}
              </div>
            </div>

            {selectedSection && docSections[selectedSection]?.subsections && (
              <div className="pretrip-subsection-selector">
                <h3>Study Subsections</h3>
                <div className="pretrip-subsection-buttons">
                  <button
                    className={`pretrip-subsection-btn ${selectedSubsection === null ? "active" : ""}`}
                    onClick={() => {
                      setSelectedSubsection(null);
                      stopSpeaking();
                    }}
                  >
                    📚 Full Section
                  </button>
                  {docSections[selectedSection].subsections.map((subsection, idx) => (
                    <button
                      key={idx}
                      className={`pretrip-subsection-btn ${selectedSubsection === subsection.name ? "active" : ""}`}
                      onClick={() => {
                        setSelectedSubsection(subsection.name);
                        stopSpeaking();
                        setCurrentChunkIndex(-1);
                      }}
                    >
                      {subsection.name}
                    </button>
                  ))}
                </div>
              </div>
            )}


            {selectedSection ? (
              <div key={selectedSection} className="pretrip-doc-section" id={`doc-${selectedSection}`} style={{ borderLeftColor: docSections[selectedSection]?.color }}>
                <h3 style={{ backgroundColor: docSections[selectedSection]?.color }}>
                  <span className="pretrip-doc-section-icon">{docSections[selectedSection]?.icon}</span>
                  {docSections[selectedSection]?.title}
                </h3>

                <div className="pretrip-doc-table-wrapper">
                  {docSections[selectedSection]?.subsections ? (
                    <div className="pretrip-subsection-list">
                      {docSections[selectedSection].subsections.map((subsection, sidx) => (
                        (selectedSubsection === null || selectedSubsection === subsection.name) && (
                          <div key={sidx} className="pretrip-subsection-block">
                            {selectedSubsection === null && <h4 className="pretrip-subsection-title">{subsection.name}</h4>}
                            <table className="pretrip-doc-table">
                              <tbody>
                                {subsection.items.map((item, i) => {
                                  const isHighlighted = currentChunkIndex >= 0 && chunkToItemMap[currentChunkIndex]?.text === item.text;
                                  return (
                                    <tr
                                      key={i}
                                      className={`${item.critical ? "pretrip-doc-critical" : ""} ${isHighlighted ? "pretrip-doc-highlighting" : ""}`}
                                      ref={isHighlighted ? setHighlightedRowRef : null}
                                    >
                                      <td className="pretrip-doc-item">
                                        {item.critical && <span className="pretrip-critical-badge">⚠️</span>}
                                        {getBusStatuses(item.text).map((status, idx) => {
                                          const badge = getBusStatusBadge(status);
                                          return badge ? (
                                            <span
                                              key={idx}
                                              className="pretrip-bus-badge"
                                              style={{ backgroundColor: badge.color }}
                                              title={badge.label}
                                            >
                                              {badge.icon}
                                            </span>
                                          ) : null;
                                        })}
                                        {isHighlighted && <span className="pretrip-reading-indicator">▶️</span>}
                                        {item.text}
                                      </td>
                                      <td className="pretrip-doc-yn">Y</td>
                                      <td className="pretrip-doc-yn">N</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )
                      ))}
                    </div>
                  ) : (
                    <table className="pretrip-doc-table">
                      <tbody>
                        {docSections[selectedSection]?.items.map((item, i) => {
                          const isHighlighted = currentChunkIndex >= 0 && chunkToItemMap[currentChunkIndex]?.text === item.text;
                          return (
                            <tr
                              key={i}
                              className={`${item.critical ? "pretrip-doc-critical" : ""} ${isHighlighted ? "pretrip-doc-highlighting" : ""}`}
                              ref={isHighlighted ? setHighlightedRowRef : null}
                            >
                              <td className="pretrip-doc-item">
                                {item.critical && <span className="pretrip-critical-badge">⚠️</span>}
                                {getBusStatuses(item.text).map((status, idx) => {
                                  const badge = getBusStatusBadge(status);
                                  return badge ? (
                                    <span
                                      key={idx}
                                      className="pretrip-bus-badge"
                                      style={{ backgroundColor: badge.color }}
                                      title={badge.label}
                                    >
                                      {badge.icon}
                                    </span>
                                  ) : null;
                                })}
                                {isHighlighted && <span className="pretrip-reading-indicator">▶️</span>}
                                {item.text}
                              </td>
                              <td className="pretrip-doc-yn">Y</td>
                              <td className="pretrip-doc-yn">N</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {(isSpeaking || isPaused) && highlightedRowRef && (
                  <div className="pretrip-floating-controls">
                    <button
                      className={`pretrip-float-btn pause-btn ${isPaused ? "resumed" : ""}`}
                      onClick={pauseSpeaking}
                      title={isPaused ? "Resume reading" : "Pause reading"}
                    >
                      {isPaused ? "▶️ Resume" : "⏸️ Pause"}
                    </button>
                    <button
                      className={`pretrip-float-btn repeat-btn ${repeatMode ? "active" : ""}`}
                      onClick={() => {
                        if (!repeatMode) {
                          setRepeatMode(true);
                          setRepeatCount(1);
                        } else {
                          setRepeatMode(false);
                          setRepeatCount(0);
                        }
                      }}
                      title={repeatMode ? "Disable repeat" : "Enable repeat"}
                    >
                      🔁 Repeat {repeatMode ? `(${repeatCount}x)` : ""}
                    </button>
                    {repeatMode && selectedSection && (
                      <button
                        className="pretrip-float-btn next-btn"
                        onClick={goToNextSection}
                        title="Move to next section"
                      >
                        → Next Section
                      </button>
                    )}
                    <button
                      className="pretrip-float-btn stop-btn"
                      onClick={stopSpeaking}
                      title="Stop reading"
                    >
                      ⏹️ Stop
                    </button>
                  </div>
                )}
              </div>
            ) : (
              Object.entries(docSections).map(([sectionId, section]) => (
              <div key={sectionId} className="pretrip-doc-section" id={`doc-${sectionId}`} style={{ borderLeftColor: section.color }}>
                <h3 style={{ backgroundColor: section.color }}>
                  <span className="pretrip-doc-section-icon">{section.icon}</span>
                  {section.title}
                </h3>

                <div className="pretrip-doc-table-wrapper">
                  <table className="pretrip-doc-table">
                    <tbody>
                      {section.subsections ? (
                        section.subsections.flatMap((subsection, sidx) =>
                          subsection.items.map((item, i) => {
                            const isHighlighted = currentChunkIndex >= 0 && chunkToItemMap[currentChunkIndex]?.text === item.text;
                            return (
                              <tr
                                key={`${sidx}-${i}`}
                                className={`${item.critical ? "pretrip-doc-critical" : ""} ${isHighlighted ? "pretrip-doc-highlighting" : ""}`}
                                ref={isHighlighted ? setHighlightedRowRef : null}
                              >
                                <td className="pretrip-doc-item">
                                  {item.critical && <span className="pretrip-critical-badge">⚠️</span>}
                                  {getBusStatuses(item.text).map((status, idx) => {
                                    const badge = getBusStatusBadge(status);
                                    return badge ? (
                                      <span
                                        key={idx}
                                        className="pretrip-bus-badge"
                                        style={{ backgroundColor: badge.color }}
                                        title={badge.label}
                                      >
                                        {badge.icon}
                                      </span>
                                    ) : null;
                                  })}
                                  {isHighlighted && <span className="pretrip-reading-indicator">▶️</span>}
                                  {item.text}
                                </td>
                                <td className="pretrip-doc-yn">Y</td>
                                <td className="pretrip-doc-yn">N</td>
                              </tr>
                            );
                          })
                        )
                      ) : (
                        section.items?.map((item, i) => {
                          const isHighlighted = currentChunkIndex >= 0 && chunkToItemMap[currentChunkIndex]?.text === item.text;
                          return (
                            <tr
                              key={i}
                              className={`${item.critical ? "pretrip-doc-critical" : ""} ${isHighlighted ? "pretrip-doc-highlighting" : ""}`}
                              ref={isHighlighted ? setHighlightedRowRef : null}
                            >
                              <td className="pretrip-doc-item">
                                {item.critical && <span className="pretrip-critical-badge">⚠️</span>}
                                {getBusStatuses(item.text).map((status, idx) => {
                                  const badge = getBusStatusBadge(status);
                                  return badge ? (
                                    <span
                                      key={idx}
                                      className="pretrip-bus-badge"
                                      style={{ backgroundColor: badge.color }}
                                      title={badge.label}
                                    >
                                      {badge.icon}
                                    </span>
                                  ) : null;
                                })}
                                {isHighlighted && <span className="pretrip-reading-indicator">▶️</span>}
                                {item.text}
                              </td>
                              <td className="pretrip-doc-yn">Y</td>
                              <td className="pretrip-doc-yn">N</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>

                  {(isSpeaking || isPaused) && highlightedRowRef && (
                    <div className="pretrip-floating-controls">
                      <button
                        className={`pretrip-float-btn pause-btn ${isPaused ? "resumed" : ""}`}
                        onClick={pauseSpeaking}
                        title={isPaused ? "Resume reading" : "Pause reading"}
                      >
                        {isPaused ? "▶️ Resume" : "⏸️ Pause"}
                      </button>
                      <button
                        className={`pretrip-float-btn repeat-btn ${repeatMode ? "active" : ""}`}
                        onClick={() => {
                          if (!repeatMode) {
                            setRepeatMode(true);
                            setRepeatCount(1);
                          } else {
                            setRepeatMode(false);
                            setRepeatCount(0);
                          }
                        }}
                        title={repeatMode ? "Disable repeat" : "Enable repeat"}
                      >
                        🔁 Repeat {repeatMode ? `(${repeatCount}x)` : ""}
                      </button>
                      {repeatMode && selectedSection && (
                        <button
                          className="pretrip-float-btn next-btn"
                          onClick={goToNextSection}
                          title="Move to next section"
                        >
                          → Next Section
                        </button>
                      )}
                      <button
                        className="pretrip-float-btn stop-btn"
                        onClick={stopSpeaking}
                        title="Stop reading"
                      >
                        ⏹️ Stop
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
            )}
          </section>
        )}

        {/* STUDY PLAN */}
        {activeView === "studyplan" && (
          <section className="pretrip-view pretrip-studyplan-view">
            <div className="pretrip-studyplan-header">
              <h2>🎯 7-Day Intensive Study Plan</h2>
              <p>Master 57 inspection items before exam day</p>
              <div className="pretrip-studyplan-countdown">
                ⏰ EXAM: Next Friday (7 Days) | Status: Study Mode Activated
              </div>
            </div>

            <div className="pretrip-studyplan-stats">
              <div className="pretrip-stat-card">
                <div className="pretrip-stat-num">57</div>
                <div className="pretrip-stat-label">Total Items</div>
              </div>
              <div className="pretrip-stat-card">
                <div className="pretrip-stat-num">41</div>
                <div className="pretrip-stat-label">Critical Items</div>
              </div>
              <div className="pretrip-stat-card">
                <div className="pretrip-stat-num">9</div>
                <div className="pretrip-stat-label">Inspection Zones</div>
              </div>
              <div className="pretrip-stat-card">
                <div className="pretrip-stat-num">100%</div>
                <div className="pretrip-stat-label">Your Goal</div>
              </div>
            </div>

            {studyPlanDays.map((day, idx) => (
              <div key={idx} className="pretrip-studyplan-day">
                <div className="pretrip-day-header">
                  <div className="pretrip-day-num">{day.day}</div>
                  <div className="pretrip-day-info">
                    <h3>{day.title}</h3>
                    <p>{day.subtitle}</p>
                  </div>
                </div>
                {day.sessions.map((session, sidx) => (
                  <div key={sidx} className="pretrip-session">
                    <div className="pretrip-session-header">
                      <span className="pretrip-session-emoji">{session.emoji}</span>
                      <span className="pretrip-session-time">{session.time}</span>
                    </div>
                    <ul className="pretrip-session-list">
                      {session.tasks.map((task, tidx) => (
                        <li key={tidx}>{task}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}

            <div className="pretrip-studyplan-critical">
              <h3>⚠️ Critical PSI Values (High-Test Frequency)</h3>
              <div className="pretrip-psi-grid">
                {criticalPsiValues.map((item, idx) => (
                  <div key={idx} className="pretrip-psi-card">
                    <strong>{item.label}:</strong> {item.value}
                  </div>
                ))}
              </div>
            </div>

            <div className="pretrip-studyplan-tips">
              <h3>🧠 Memory Techniques</h3>
              <div className="pretrip-tips-grid">
                {memoryTechniques.map((tech, idx) => (
                  <div key={idx} className="pretrip-tip-card">
                    <strong>{tech.name}</strong>
                    <p>{tech.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="pretrip-studyplan-success">
              <h3>✨ Exam Day Success Tips</h3>
              <ul className="pretrip-success-list">
                <li>🌅 Arrive early – calm your mind</li>
                <li>📖 Read carefully – wording matters</li>
                <li>💪 Trust your prep – you know this inside & out</li>
                <li>⏱️ Pace yourself – don't rush</li>
                <li>🚩 Flag hard ones – come back to them</li>
                <li>✅ Double-check work – review every answer</li>
              </ul>
            </div>
          </section>
        )}
      </main>

      <footer className="pretrip-footer">
        Study tool for personal test prep – good luck out there.
      </footer>

      {showBusGuide && (
        <div className="pretrip-modal-overlay" onClick={() => setShowBusGuide(false)}>
          <div className="pretrip-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pretrip-modal-header">
              <h2>📋 Bus Start/Stop Sequence</h2>
              <button className="pretrip-modal-close" onClick={() => setShowBusGuide(false)}>✕</button>
            </div>
            <div className="pretrip-modal-content">
              <div className="bus-guide-section" onClick={() => { setSelectedSection("cabin"); setShowBusGuide(false); }} style={{ cursor: "pointer" }}>
                <h3>Phase 1: Driver's Cabin (BUS RUNNING) →</h3>
                <p><strong>1. TURN TO NIGHT RUN</strong> (electrical only, not started)</p>
                <ul>
                  <li>Check ABS Fail light turns on and off</li>
                </ul>
                <p><strong>2. START THE BUS</strong></p>
                <ul>
                  <li>Verify DEF is above 30%</li>
                  <li>All light and control checks</li>
                </ul>
              </div>

              <div className="bus-guide-section" onClick={() => { setSelectedSection("brakes"); setShowBusGuide(false); }} style={{ cursor: "pointer" }}>
                <h3>Phase 2: Brake Tests (ENGINE RUNNING) →</h3>
                <p><strong>3. KEEP ENGINE RUNNING</strong></p>
                <ul>
                  <li>Parking brake test (roll 1-2 feet)</li>
                  <li>Service brake test (drive 5 mph, apply brake)</li>
                </ul>
                <p><strong>4. TURN ENGINE OFF & CHOCK THE BUS</strong></p>
              </div>

              <div className="bus-guide-section" onClick={() => { setSelectedSection("air"); setShowBusGuide(false); }} style={{ cursor: "pointer" }}>
                <h3>Phase 3: Air Brake Tests (ENGINE OFF, BUS CHOCKED) →</h3>
                <p><strong>5. KEEP ENGINE OFF</strong></p>
                <ul>
                  <li>Air leakage rate test</li>
                  <li>Governor Cut-in and Cut-out</li>
                  <li>Static test (Release parking brake)</li>
                  <li>Applied test (Apply service brake)</li>
                  <li>Low air warning test</li>
                  <li>Spring brake test</li>
                </ul>
              </div>

              <div className="bus-guide-section" onClick={() => { setSelectedSection("air"); setShowBusGuide(false); }} style={{ cursor: "pointer" }}>
                <h3>Phase 4: Build-Up Rate Test →</h3>
                <p><strong>6. START THE BUS AGAIN</strong></p>
                <ul>
                  <li>Build 85-100 PSI in 45 seconds</li>
                </ul>
              </div>

              <div className="bus-guide-section" onClick={() => { setSelectedSection("air"); setShowBusGuide(false); }} style={{ cursor: "pointer" }}>
                <h3>Phase 5: End of Inspection →</h3>
                <p><strong>7. O.D.S THE BUS</strong></p>
                <ul>
                  <li><strong>O</strong>pen the door</li>
                  <li><strong>D</strong>ump the air</li>
                  <li><strong>S</strong>top the bus</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* DATA */
const facts = [
  ["Governor cut-in", "Pump to 100 psi; cuts in when needle moves right at idle"],
  ["Governor cut-out", "120-140 psi (you'll hear a sneeze)"],
  ["Static leak test", "≤ 2 psi lost in 1 minute, brakes released"],
  ["Applied leak test", "≤ 3 psi lost in 1 minute, brakes applied"],
  ["Low air warning", "Light/buzzer on at or before 55 psi"],
  ["Spring brake engages", "Between 20-45 psi"],
  ["Air build-up rate", "85 - 100 psi within 45 seconds"],
  ["DEF level", "Must verify above 30%"],
  ["Tread depth", '4/32" - checked with tread depth gauge'],
  ["Tire inflation", "120 psi on every tire"],
  ["Lug nuts", "10 per wheel, properly tightened"],
  ["Fire extinguishers", "1, properly charged and secured"],
  ["Reflective triangles", "3"],
  ["Spare fuses", "None - circuit breakers under driver's window"],
  ["Emergency exits", "9 total: 2 roof hatches, 2 doors, 5 windows"],
  ["Front lights", "5 amber clearance, 2 headlights, 2 directional/hazard"],
  ["Left side lights", "2 amber clearance, 1 red marker, 2 directional/hazard"],
  ["Rear lights", "5 red marker, 2 reverse, 2 brake, 2 directional/hazard"],
  ["Right side lights", "1 red marker, 2 amber clearance, 2 directional/hazard"],
];

const phrases = [
  ["Night-run lighting", "The bus is in night run so all of the clearance, marker, low beams and taillights are on."],
  ["All-lights test", "I will activate my all lights test function. The reverse lights and alarm will come on. My bus is not in reverse."],
  ["Service brake result", "The bus did not pull left or right or have any unusual feel, and came to a complete stop."],
  ["Air leakage (engine off)", "I hear no audible air leaks."],
  ["Governor cut-out", "My governor cut out at ___ psi."],
  ["Compounding brakes", "State you know you're not supposed to compound brakes, but must to put the bus in gear."],
];

const gaugeStops = [
  { label: "Idle / Cut-in build (100 psi)", value: 100, angle: 35 },
  { label: "Low air warning (55 psi)", value: 55, angle: -35 },
  { label: "Spring brake window (20-45 psi)", value: 32, angle: -55 },
  { label: "Governor cut-out (130 psi)", value: 130, angle: 70 },
];

const studyPlanDays = [
  {
    day: 1,
    title: "Monday - Foundation & Cabin Mastery",
    subtitle: "Master the 19 cabin items (longest section)",
    sessions: [
      {
        emoji: "☀️",
        time: "Morning (1.5 hrs)",
        tasks: [
          "Read intro banner (3 points of contact)",
          "Read Cabin section with voice",
          "Pause after each item, repeat 3 times aloud",
          "Understand WHY each check matters"
        ]
      },
      {
        emoji: "🌤️",
        time: "Afternoon (1.5 hrs)",
        tasks: [
          "Use Flashcards: Cabin section only",
          "Answer BEFORE flipping card",
          "Track difficult items"
        ]
      },
      {
        emoji: "🌙",
        time: "Evening (30 min)",
        tasks: [
          "Quiz mode: Cabin items",
          "Target: 90%+"
        ]
      }
    ]
  },
  {
    day: 2,
    title: "Tuesday - Brakes & Air Brakes (Part 1)",
    subtitle: "Learn brake procedures + first 4 air brake tests",
    sessions: [
      {
        emoji: "☀️",
        time: "Morning (1.5 hrs)",
        tasks: [
          "Read Brakes section with voice",
          "Learn exact wording for parking & service brake tests",
          "Learn instruction: 'Back the bus up to starting point...'",
          "Pause and repeat each item 3x"
        ]
      },
      {
        emoji: "🌤️",
        time: "Afternoon (1.5 hrs)",
        tasks: [
          "Read Air Brakes (Governor Cut-in → Applied test)",
          "CRITICAL section - heavily tested",
          "Write key numbers: 100 psi, 120-140 psi, 2 psi, 3 psi"
        ]
      },
      {
        emoji: "🌙",
        time: "Evening (30 min)",
        tasks: [
          "Flashcards: Brakes + Air (1/2)",
          "Focus on accuracy",
          "Target: 85%+"
        ]
      }
    ]
  },
  {
    day: 3,
    title: "Wednesday - Air Brakes (Part 2) + Internal",
    subtitle: "Complete air brakes + learn emergency exits",
    sessions: [
      {
        emoji: "☀️",
        time: "Morning (1.5 hrs)",
        tasks: [
          "Read Air Brakes (Low warning → ODS) with voice",
          "Learn psi ranges: 55, 20-45, 85-100 in 45 sec",
          "Record yourself saying each test",
          "Listen back to check accuracy"
        ]
      },
      {
        emoji: "🌤️",
        time: "Afternoon (1.5 hrs)",
        tasks: [
          "Read Internal Pre-Trip with voice",
          "Emergency exits: 9 total (2 roof, 2 doors, 5 windows)",
          "Memorize exact descriptions"
        ]
      },
      {
        emoji: "🌙",
        time: "Evening (30 min)",
        tasks: [
          "Quiz mode: Air Brakes + Internal",
          "Target: 95%+"
        ]
      }
    ]
  },
  {
    day: 4,
    title: "Thursday - External Sections",
    subtitle: "Front, Left, Rear, Engine, Right (30 items)",
    sessions: [
      {
        emoji: "☀️",
        time: "Morning (2 hrs)",
        tasks: [
          "Read FRONT + LEFT SIDE with voice",
          "LEFT SIDE is longest (12 items) - focus here",
          "Memorize exact light counts"
        ]
      },
      {
        emoji: "🌤️",
        time: "Afternoon (1.5 hrs)",
        tasks: [
          "Read REAR + ENGINE + RIGHT with voice",
          "Learn: Lights, Battery/Electrical, DEF, Fuel"
        ]
      },
      {
        emoji: "🌙",
        time: "Evening (45 min)",
        tasks: [
          "Flashcards: External sections",
          "Draw the bus, label each light location",
          "Target: 90%+"
        ]
      }
    ]
  },
  {
    day: 5,
    title: "Friday - Full Review + Quiz Domination",
    subtitle: "Practice full document + identify weak areas",
    sessions: [
      {
        emoji: "☀️",
        time: "Morning (1.5 hrs)",
        tasks: [
          "Listen to ENTIRE document with voice (no pausing)",
          "Let it flow, absorb the rhythm",
          "Watch highlighting follow along"
        ]
      },
      {
        emoji: "🌤️",
        time: "Afternoon (1.5 hrs)",
        tasks: [
          "FULL EXAM SIMULATION: All sections",
          "Answer every question from memory",
          "Review wrong answers immediately",
          "Target: 90%+"
        ]
      },
      {
        emoji: "🌙",
        time: "Evening (45 min)",
        tasks: [
          "Targeted flashcards: Items you missed",
          "Repeat each 5 times",
          "Note patterns in mistakes"
        ]
      }
    ]
  },
  {
    day: 6,
    title: "Saturday - Targeted Deep Dive + Final Exam Sim",
    subtitle: "Fix weak areas + prove readiness",
    sessions: [
      {
        emoji: "☀️",
        time: "Morning (2 hrs)",
        tasks: [
          "Listen to problem sections (pause frequently)",
          "Drill difficult items 5x each",
          "Write them down if still struggling"
        ]
      },
      {
        emoji: "🌤️",
        time: "Afternoon (1.5 hrs)",
        tasks: [
          "FULL EXAM SIMULATION: All sections",
          "Time yourself (realistic pace)",
          "No pausing or rewinding",
          "Target: 95%+"
        ]
      },
      {
        emoji: "🌙",
        time: "Evening (45 min)",
        tasks: [
          "Review final quiz results",
          "Targeted review of problem areas only",
          "Sleep early - you need rest!"
        ]
      }
    ]
  },
  {
    day: 7,
    title: "Sunday - Rest & Refresh",
    subtitle: "Stay sharp without cramming",
    sessions: [
      {
        emoji: "☀️",
        time: "Morning (1 hr)",
        tasks: [
          "Light review: Cabin + Brakes only",
          "Don't overload your brain",
          "Maintain confidence"
        ]
      },
      {
        emoji: "🌤️",
        time: "Midday (30 min)",
        tasks: [
          "One final quiz: All sections",
          "Target: 95%+"
        ]
      },
      {
        emoji: "🌙",
        time: "Afternoon/Evening",
        tasks: [
          "REST & RECOVER",
          "Review your strongest section (confidence builder)",
          "Prepare materials for exam",
          "Get good sleep - you've earned it!"
        ]
      }
    ]
  }
];

const criticalPsiValues = [
  { label: "Governor Cut-in", value: "100 psi" },
  { label: "Governor Cut-out", value: "120–140 psi" },
  { label: "Low Air Warning", value: "55 psi" },
  { label: "Spring Brake Engagement", value: "20–45 psi" },
  { label: "Air Build-up Test", value: "85–100 psi in 45 seconds" },
  { label: "Static Leak Test", value: "≤ 2 psi loss (1 minute)" },
  { label: "Applied Leak Test", value: "≤ 3 psi loss (1 minute)" },
  { label: "Tire Tread", value: "4/32\"" },
  { label: "Tire Pressure", value: "120 psi" },
  { label: "Lug Nuts", value: "10 per wheel" },
  { label: "DEF Level", value: "Above 30%" },
  { label: "Emergency Exits", value: "9 total" }
];

const memoryTechniques = [
  { name: "🔄 Spaced Repetition", desc: "Read → Wait 30min → Review → Next day → Before exam" },
  { name: "🎲 Chunking", desc: "Group into memorable sets (Cabin, Brakes, Air, External)" },
  { name: "🏛️ Memory Palace", desc: "Imagine walking around the bus during inspection" },
  { name: "🗣️ Active Recall", desc: "Cover text, answer FIRST, then check" },
  { name: "👂 Auditory Learning", desc: "Listen to voice reading multiple times" },
  { name: "✍️ Kinesthetic Learning", desc: "Write key numbers on index cards" }
];

const flashcards = [
  ["What percent must DEF be verified above?", "Above 30%"],
  ["At what psi does the governor cut in?", "Pumped to 100 psi; cuts in when needle moves right at idle"],
  ["At what psi range does the governor cut out?", "120-140 psi (you'll hear a sneeze)"],
  ["Max psi loss in the STATIC leak test (1 min)?", "No more than 2 psi"],
  ["Max psi loss in the APPLIED leak test (1 min)?", "No more than 3 psi"],
  ["At what psi should the low air warning activate?", "At or before 55 psi"],
  ["At what psi range does the spring brake engage?", "Between 20-45 psi"],
  ["Air build-up test: from/to what psi, in how long?", "85 - 100 psi within 45 seconds"],
  ["How many fire extinguishers, and what condition?", "1, properly charged and secured"],
  ["How many reflective triangles?", "3"],
  ["Do the buses have spare electrical fuses?", "No - circuit breakers, located under the driver's window"],
  ["Total number of emergency exits on the bus?", "9"],
  ["How many roof hatches, and how do you escape?", "2 - turn the red knob and push up to exit"],
  ["How many emergency doors, and how do you escape?", "2 - break the glass and turn the red valve"],
  ["How many emergency windows (marked with *)?", "5 - point and demonstrate one"],
  ["Tread depth requirement for tires?", '4/32" - checked with a tread depth gauge'],
  ["Required tire inflation, all tires?", "120 psi - checked with a tire gauge"],
  ["How many lug nuts per wheel?", "10, all present and properly tightened"],
  ["Front: how many amber clearance lights?", "5"],
  ["Front: how many clear headlights?", "2"],
  ["Left side: how many red marker lights?", "1"],
  ["Left side: how many amber clearance lights?", "2"],
  ["Rear: how many red marker lights?", "5"],
  ["Rear: how many white reverse lights?", "2"],
  ["Rear: how many red brake lights?", "2"],
  ["Right side: how many red marker lights?", "1"],
  ["Right side: how many amber clearance lights?", "2"],
  ["What do you say about night-run lighting?", "The bus is in night run so all of the clearance, marker, low beams and taillights are on."],
  ["What do you say activating the all-lights test?", "I will activate my all lights test function. The reverse lights and alarm will come on. My bus is not in reverse."],
  ["How do you activate the all-lights function?", "Step on both directionals at the same time"],
  ["What do you announce after the service brake test?", "The bus did not pull left or right or have any unusual feel, and came to a complete stop."],
  ["What do you say during the air leakage rate test?", "I hear no audible air leaks"],
  ["What speed for the service brake test?", "5 mph, then apply the service brake"],
  ["How far should the bus roll in the parking brake test?", "1 to 2 feet"],
  ["What do you announce before compounding brakes?", "That you know you're not supposed to, but must to put the bus in gear"],
  ["Where is the power steering fluid reservoir?", "Under the driver's seat (not accessible)"],
  ["What suspension do T buses have?", "Air ride suspension"],
  ["How should the vehicle be sitting?", "Level, front to rear and side to side"],
  ["When do you use 3 points of contact?", "Stepping into the bus at the start of the test"],
];

const quizData = [
  { q: "At what psi does the governor cut out?", opts: ["55-70 psi", "85-100 psi", "120-140 psi", "150-160 psi"], correct: 2 },
  { q: "What's the max psi loss allowed in the static leak test (1 min)?", opts: ["1 psi", "2 psi", "3 psi", "5 psi"], correct: 1 },
  { q: "What's the max psi loss allowed in the applied leak test (1 min)?", opts: ["2 psi", "3 psi", "4 psi", "6 psi"], correct: 1 },
  { q: "At what psi must the low air warning activate?", opts: ["At or before 45 psi", "At or before 55 psi", "At or before 65 psi", "At or before 75 psi"], correct: 1 },
  { q: "Spring brakes should engage in what psi range?", opts: ["10-20 psi", "20-45 psi", "45-60 psi", "60-80 psi"], correct: 1 },
  { q: "Air build-up test: 85 psi to 100 psi should take how long?", opts: ["20 seconds", "30 seconds", "45 seconds", "60 seconds"], correct: 2 },
  { q: "DEF level must be verified above what percent?", opts: ["10%", "20%", "30%", "50%"], correct: 2 },
  { q: "Required tread depth for tires?", opts: ['2/32"', '4/32"', '6/32"', '8/32"'], correct: 1 },
  { q: "Required tire inflation (all tires)?", opts: ["100 psi", "110 psi", "120 psi", "130 psi"], correct: 2 },
  { q: "How many lug nuts per wheel?", opts: ["8", "10", "12", "6"], correct: 1 },
  { q: "How many total emergency exits does the bus have?", opts: ["7", "8", "9", "10"], correct: 2 },
  { q: "How many roof hatches are there?", opts: ["1", "2", "3", "4"], correct: 1 },
  { q: "How many reflective triangles are in the emergency box?", opts: ["2", "3", "4", "5"], correct: 1 },
  { q: "Do these buses carry spare electrical fuses?", opts: ["Yes, 2 spares", "Yes, 5 spares", "No - they use circuit breakers", "No - none provided at all"], correct: 2 },
  { q: "How many amber clearance lights are on the front?", opts: ["2", "3", "4", "5"], correct: 3 },
  { q: "How many red marker lights are on the rear of the bus?", opts: ["2", "3", "4", "5"], correct: 3 },
  { q: "How far should the bus roll during the parking brake test?", opts: ["0 feet", "1 to 2 feet", "3 to 4 feet", "5 feet"], correct: 1 },
  { q: "What speed do you drive at for the service brake test?", opts: ["3 mph", "5 mph", "10 mph", "15 mph"], correct: 1 },
  { q: "How do you activate the all-lights test function?", opts: ["Pull the parking brake twice", "Step on both directionals at the same time", "Flash the high beams", "Press the horn twice"], correct: 1 },
  { q: "Where is the power steering fluid reservoir located?", opts: ["Under the hood", "Under the driver's seat", "Behind the dashboard", "In the engine compartment door"], correct: 1 },
  { q: "What does ABS stand for?", opts: ["Anti-Brake System", "Anti-Lock Braking System", "Automatic Brake Service", "Air Brake Seal"], correct: 1 },
  { q: "How do you exit through a roof hatch in emergency?", opts: ["Push down", "Turn red knob and push up", "Break the glass", "Turn the red valve"], correct: 1 },
  { q: "What color are the emergency exit handles?", opts: ["Blue", "Red", "Yellow", "Green"], correct: 1 },
  { q: "What PSI range is considered the spring brake engagement zone?", opts: ["55-85 psi", "20-45 psi", "45-70 psi", "100-120 psi"], correct: 1 },
  { q: "How long should it take to build from 85-100 psi?", opts: ["30 seconds", "45 seconds", "60 seconds", "90 seconds"], correct: 1 },
  { q: "What is the minimum tread depth for CDL tires?", opts: ["2/32 inch", "3/32 inch", "4/32 inch", "5/32 inch"], correct: 2 },
  { q: "How many mirrors must be checked as clean and properly adjusted?", opts: ["1", "2", "3", "4"], correct: 2 },
  { q: "What does DEF stand for?", opts: ["Diesel Engine Fluid", "Diesel Exhaust Fluid", "Direct Exhaust Fuel", "Diesel Efficiency Filter"], correct: 1 },
  { q: "At what percentage should DEF level be verified above?", opts: ["20%", "30%", "40%", "50%"], correct: 1 },
  { q: "How many fire extinguishers must be on the bus?", opts: ["0", "1", "2", "3"], correct: 1 },
  { q: "Where are circuit breakers located on these buses?", opts: ["Under the hood", "Under driver's window", "In engine compartment", "Under the seat"], correct: 1 },
  { q: "What should you check for on brake pads?", opts: ["Cracks only", "Grease or oil contamination", "Rust only", "Color change"], correct: 1 },
  { q: "How do you exit through an emergency door?", opts: ["Push it open", "Break glass and turn red valve", "Turn the handle", "Kick it open"], correct: 1 },
  { q: "How many total windows are emergency exits?", opts: ["3", "4", "5", "6"], correct: 2 },
  { q: "What color must the front lights be?", opts: ["Red and amber", "Amber and clear", "Red only", "Clear only"], correct: 1 },
  { q: "What should you check on the windshield?", opts: ["Color", "Clean, not cracked, no obstructions except sticker"], correct: 1 },
  { q: "How many sections must be checked in the Pre-Trip Inspection?", opts: ["6", "7", "8", "9"], correct: 3 },
  { q: "What is the maximum allowed psi loss in static test per minute?", opts: ["1 psi", "2 psi", "3 psi", "4 psi"], correct: 1 },
  { q: "What is the maximum allowed psi loss in applied test per minute?", opts: ["2 psi", "3 psi", "4 psi", "5 psi"], correct: 1 },
  { q: "What speed should you drive for the parking brake test?", opts: ["No speed required", "3 mph", "5 mph", "10 mph"], correct: 2 },
  { q: "How many feet should the bus roll during parking brake test?", opts: ["0.5 to 1 feet", "1 to 2 feet", "2 to 3 feet", "3 to 4 feet"], correct: 1 },
  { q: "What must you announce about the service brake?", opts: ["Bus pulled left", "No unusual feel and complete stop", "Pulled right", "Soft feeling"], correct: 1 },
  { q: "How many amber clearance lights on the left side?", opts: ["1", "2", "3", "4"], correct: 1 },
  { q: "How many red marker lights on the left side?", opts: ["0", "1", "2", "3"], correct: 1 },
  { q: "What should power steering fluid level be?", opts: ["Below add mark", "Between add and full marks", "Above full mark", "Doesn't matter"], correct: 1 },
  { q: "What should you check on steering joints and sockets?", opts: ["Color", "Not worn or loose", "Size only", "Temperature"], correct: 1 },
  { q: "How many red marker lights are on the rear?", opts: ["2", "3", "4", "5"], correct: 3 },
  { q: "How many red auxiliary brake lights on rear?", opts: ["1", "2", "3", "4"], correct: 1 },
  { q: "How many white reverse lights on rear?", opts: ["1", "2", "3", "4"], correct: 1 },
];

const docSections = {
  opening: {
    icon: "🚌",
    color: "#ffb020",
    title: "Pre-Trip Inspection - Begin Test",
    items: [
      { text: "Begin test without chocks on the tire.", critical: true },
      { text: "Step in the bus by using 3 points of contact and turn the door master switch off.", critical: true },
      { text: "Then get in the seat and apply seatbelt.", critical: true },
      { text: "Put the air on the door and close the door.", critical: true },
    ]
  },
  cabin: {
    icon: "🚪",
    color: "#3498db",
    title: "Driver's Cabin",
    subsections: [
      {
        name: "Safety & Entry",
        items: [
          { text: "Turn the bus to night run and check that the ABS Fail light turns on and off", critical: true },
          { text: "Start the bus and verify that the DEF is above 30%", critical: true },
          { text: "Mirrors - Clean, not cracked and no obstructions; adjusted properly from the inside", critical: false },
          { text: "Heater / Climate Control - Turn ON/OFF to show it is working properly", critical: false },
          { text: "Wheelchair Ramp - (offer to demonstrate) say: Wheelchair ramp is working properly, not damaged, no missing or broken parts", critical: true },
          { text: "Front doors - open to show that they open smoothly and close securely", critical: true },
          { text: "Step light - Located above the front door and working properly; tread is not worn or loose. No tripping hazards; handrails are secure", critical: true },
        ]
      },
      {
        name: "Lights & Controls",
        items: [
          { text: "Four way hazards - activate", critical: false },
          { text: "Defroster - Turn ON and OFF: It works properly and blows warm air", critical: false },
          { text: "Wipers - Turn ON/OFF; wiper arms/blades are secure, not damaged and operate smoothly", critical: false },
          { text: "Washer fluid - Push button to demonstrate wiper fluid is full and working properly", critical: false },
          { text: "Windshield clean, not cracked and no obstructions except for the valid inspection sticker", critical: false },
          { text: "Horn - Tap on it to show it's working properly", critical: false },
          { text: "High beams - activate", critical: false },
          { text: "Left directional - activate", critical: false },
          { text: "Right directional - activate", critical: false },
          { text: "Ask the trooper to step outside to assist with external lights inspection. Tell the examiner: The bus is in night run so all of the clearance, marker, low beams and taillights are on. Also: I will activate my all lights test function. The reverse lights and alarm will come on. My bus is not in reverse.", critical: true },
          { text: "Step on both directionals at the same time to activate the all lights function", critical: true },
          { text: "Re-apply the left, right directionals, 4-ways and high beam lights", critical: true },
        ]
      },
    ]
  },
  brakes: {
    icon: "🅱️",
    color: "#e74c3c",
    title: "Parking & Service Brake Tests",
    items: [
      { text: "Parking brake test - Make sure to build up the air for this. Notify the examiner that you know you aren't supposed to compound your brakes but, in order to put the bus in gear, you have to. Place the bus in drive, release the parking brake and let the bus roll 1-2 feet, then reapply the parking brake. Announce that the brake worked when the bus is stopped and put the bus back into neutral. Build a full tank of air.", critical: true },
      { text: "Service brake test - Once you have a full tank, put the bus in drive and release the parking brake. Drive forward at 5 mph then apply the service brake. Apply the parking brake and put the bus in neutral. Announce that The bus did not pull left or right or have any unusual feel, and came to a complete stop.", critical: true },
      { text: "Back the bus up to the starting point behind the yellow box. Turn the bus off and chock the bus. Then begin the rest of the air brakes.", critical: true },
    ]
  },
  air: {
    icon: "💨",
    color: "#9b59b6",
    title: "Air Brakes",
    subsections: [
      {
        name: "Governor Tests (PSI 100 & 120-140)",
        items: [
          { text: "Governor Cut-in: Build to a full tank of air. Release the parking brake. Pump the service brake to bring the air pressure to 100 psi. Monitor the gauge - the governor has cut in when the needle starts moving to the right at idle. Reapply the parking brake as soon as the needle starts moving to the right.", critical: true },
          { text: "Governor Cut-out: Build air pressure to governor cut-out, approximately 120-140 psi. The needle will stop moving to the right and you will hear a sneeze. Tell the examiner what psi your governor cut out at: My governor cut out at ___ psi.", critical: true },
        ]
      },
      {
        name: "Leak Detection (2 PSI & 3 PSI)",
        items: [
          { text: "Air leakage rate test: Turn the master run switch into the stop-engine position, then back to night run. Open the window and listen for audible air leaks. Tell the examiner I hear no audible air leaks.", critical: true },
          { text: "Static test: Release the parking brake. Initial loss will be 5-15 psi. Tell Examiner how many initial psi were lost. Then tell the trooper you will be monitoring your watch and the gauge once the needle stabilizes for one minute. You should lose no more than 2 psi in 1 minute. After 1 minute, tell the examiner how many psi you lost, if any.", critical: true },
          { text: "Applied test: Apply firm pressure to the service brake and hold. Initial loss will be 5-15 psi. Tell Examiner how many initial psi were lost. Then tell the examiner you will monitor your watch and the gauge once the needle stabilizes. You should lose no more than 3 psi in 1 minute. After 1 minute, tell the examiner how many psi you lost, if any.", critical: true },
        ]
      },
      {
        name: "Warning Lights & Safety (55 & 20-45 PSI)",
        items: [
          { text: "Low air warning test: Pump down on the service brake. The low air warning light/buzzer should come on at or before 55 psi. Tell the examiner when the light comes on.", critical: true },
          { text: "Spring brake test: Continue to pump the service brake. The spring brake should engage between 20-45 psi. Tell the examiner at what psi the spring brake engaged.", critical: true },
        ]
      },
      {
        name: "Build-Up Rate (85-100 PSI in 45 Seconds)",
        items: [
          { text: "Air pressure rate build-up test: Start the bus. Tell the examiner this bus is equipped with a dead pedal. Tell the trooper you are going to build up to 85 psi, then monitor your watch and the gauge - it should build from 85 psi to 100 psi within 45 seconds. Tell the examiner how long it took once it reaches 100 psi.", critical: true },
          { text: "O.D.S the bus", critical: false },
        ]
      },
    ]
  },
  internal: {
    icon: "🔍",
    color: "#f39c12",
    title: "Internal Pre-Trip",
    items: [
      { text: "Point out all the contents inside the emergency box: 1 fire extinguisher (properly charged and secured), 3 reflective triangles. Spare fuses - our buses do not have spare electrical fuses; our buses have circuit breakers located under the driver's window.", critical: true },
      { text: "Walk up the aisle grabbing and pulling on the seats to check that the seat frames are not broken and attached to the floor or seat rail.", critical: true },
      { text: "Walk to the back of the bus and announce that the bus has 9 emergency exits: 2 roof hatches - point and explain to escape you would turn the red knob and push up to exit; 2 doors - point and explain to escape you would break the glass and turn the red valve; 5 windows - point and demonstrate one.", critical: true },
      { text: "All interior mirrors are clean, not cracked and properly adjusted. Mirror brackets are not bent, securely mounted and no missing parts.", critical: false },
    ]
  },
  front: {
    icon: "🔦",
    color: "#16a085",
    title: "External – Front",
    items: [
      { text: "Passenger and driver side mirrors are clean, not cracked. Mirror brackets are not bent and mounted securely with no missing parts.", critical: true },
      { text: "Lights, lenses and reflectors are the proper color, clean, not cracked and not missing: 5 amber clearance lights, 2 amber reflective tape, 2 clear headlights, 2 amber directional/hazard lights, 2 amber auxiliary directional/hazard lights (if present).", critical: false },
    ]
  },
  left: {
    icon: "⬅️",
    color: "#2980b9",
    title: "Left Side",
    subsections: [
      {
        name: "Lights & Visibility",
        items: [
          { text: "Lights, lenses and reflectors are the proper color, clean, not cracked and not missing: 2 amber clearance lights, 2 amber reflective tapes, 1 red marker light, 1 red reflective tape, 2 amber directional/hazard lights.", critical: false },
        ]
      },
      {
        name: "Power Steering System",
        items: [
          { text: "Power steering fluid (not accessible) - located under the driver's seat. Fluid level should be between add and full marks. Hoses not cracked, worn or leaking and secured with fittings.", critical: true },
          { text: "Power steering box (not accessible) - securely mounted and not leaking.", critical: true },
        ]
      },
      {
        name: "Steering Components",
        items: [
          { text: "Steering hoses and connections - not cracked, worn or leaking.", critical: true },
          { text: "Links, arms and rods are not worn or cracked.", critical: true },
          { text: "Joints and sockets are not worn or loose.", critical: true },
          { text: "No missing nuts, bolts or cotter keys in the steering system.", critical: true },
        ]
      },
      {
        name: "Tires & Wheels",
        items: [
          { text: 'Driver\'s side front tire - Tread depth 4/32"; tread depth would be checked with a tread depth gauge.', critical: true },
          { text: "Tire condition - no cuts or damage that expose body plies; no bumps or bulges in the sidewall.", critical: true },
          { text: "Tire inflation - all tires must be 120 psi and would be checked with a tire gauge.", critical: true },
          { text: "Valve stem is accessible, not damaged, capped and not leaking.", critical: false },
          { text: "Rims - not damaged, no cracks, dents or welds; no loose/missing nuts or bolts and no elongated bolt holes.", critical: true },
          { text: "Lug nuts - 10 lug nuts all present, properly tightened, no shiny threads or rust trails that may indicate looseness; no broken studs.", critical: true },
        ]
      },
      {
        name: "Brakes & Suspension",
        items: [
          { text: "Contaminates - no grease or oil on the brake lining or pads.", critical: true },
          { text: "Brake lines or hoses are not cracked, worn or leaking and fittings are secure.", critical: true },
          { text: "T buses have air ride suspension - airbag and shocks are secure, not damaged and not leaking.", critical: false },
          { text: "Vehicle should be sitting level front to rear and side to side.", critical: false },
        ]
      },
    ]
  },
  rear: {
    icon: "🔴",
    color: "#c0392b",
    title: "Rear – Back Of Bus",
    items: [
      { text: "Lights, lenses and reflectors are the proper color, clean, not cracked and not missing: 5 red marker lights, 2 red auxiliary brake lights, 2 amber directional/hazards, 2 red brake lights, 2 white reverse lights, 2 red reflective tapes.", critical: false },
    ]
  },
  engine: {
    icon: "⚙️",
    color: "#34495e",
    title: "Engine Compartment",
    items: [
      { text: "Frame is not damaged, no broken welds or holes.", critical: true },
      { text: "Engine coolant - level is between the add and full marks and not leaking.", critical: true },
      { text: "Engine oil - pull the dipstick, wipe it off, put it back in, pull it out again and show the level is between the add and full marks.", critical: true },
      { text: "Fluid and air leaks - no dripping fluids around the engine or puddles on the ground. Hoses and air lines are not cracked, worn or leaking.", critical: true },
    ]
  },
  right: {
    icon: "➡️",
    color: "#27ae60",
    title: "Right Side",
    items: [
      { text: "Lights, lenses and reflectors are the proper color, clean, not cracked and not missing: 1 red marker light, 2 amber clearance lights, 2 amber directional/hazard, 1 red reflective tape, 2 amber reflective tape.", critical: false },
      { text: "Battery/Electrical system - if it was accessible, I would check that: the battery and cables are secure, not cracked or worn; connections are tight and not excessively corroded; battery compartment is locked.", critical: true },
      { text: "Diesel Exhaust Fluid (DEF) is securely mounted, cap is tight, and no leaks from the line.", critical: true },
      { text: "Diesel Fuel tank is securely mounted, cap is tight, and no leaks from the tank or line.", critical: true },
    ]
  },
};
